import runAudio from '../assets/audio/run.mp3';
import failedHitAudio from '../assets/audio/failed-hit.mp3';
import successHitAudio from '../assets/audio/success-hit.mp3';
import emojiCelebrateAudio from '../assets/audio/emoji-celebrate.mp3';
import overAudio from '../assets/audio/over-1.mp3';
import readyLevelAudio from '../assets/audio/ready-level.mp3';

interface AudioConfig {
  loop?: boolean;
  volume?: number;
  useWebAudio?: boolean; // Whether to prefer Web Audio API for this sound
}

export class GameAudioManager {
  private audioContext: AudioContext | null = null;
  
  // Audio buffers for Web Audio API (for low-latency sounds)
  private audioBuffers: Map<string, AudioBuffer> = new Map();
  
  // HTML Audio elements (for background music and longer sounds)
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  
  // Audio configurations
  private audioConfigs: Map<string, AudioConfig> = new Map([
    ['failedHit', { volume: 1.0, useWebAudio: true }],
    ['successHit', { volume: 1.0, useWebAudio: true }],
    ['ufoBreak', { volume: 0.9, useWebAudio: true }],
    ['emojiCelebrate', { volume: 0.8, useWebAudio: false }],
    ['background', { volume: 1.0, loop: true, useWebAudio: false }],
    ['gameOver', { volume: 1.0, useWebAudio: false }],
    ['readyLevel', { volume: 1.0, loop: true, useWebAudio: false }]
  ]);

  public async initialize(): Promise<void> {
    if (this.audioContext) return; // Already initialized

    try {
      // Create AudioContext
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Audio file mappings
      const audioFiles = {
        failedHit: failedHitAudio,
        successHit: successHitAudio,
        ufoBreak: successHitAudio, // Reuse success sound
        emojiCelebrate: emojiCelebrateAudio,
        background: runAudio,
        gameOver: overAudio,
        readyLevel: readyLevelAudio
      };

      // Load buffers for Web Audio API and create HTML elements
      const loadPromises = Object.entries(audioFiles).map(async ([key, url]) => {
        const config = this.audioConfigs.get(key);
        
        if (config?.useWebAudio && this.audioContext) {
          // Load as AudioBuffer for Web Audio API
          try {
            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const buffer = await this.audioContext.decodeAudioData(arrayBuffer);
            this.audioBuffers.set(key, buffer);
          } catch (error) {
            console.warn(`Failed to load ${key} as AudioBuffer, will use HTMLAudioElement fallback:`, error);
          }
        }
        
        // Always create HTMLAudioElement as fallback
        const audio = new Audio(url);
        audio.load();
        if (config?.volume !== undefined) {
          audio.volume = config.volume;
        }
        if (config?.loop) {
          audio.loop = config.loop;
        }
        this.audioElements.set(key, audio);
      });

      await Promise.all(loadPromises);
      console.log('Audio system initialized successfully');
    } catch (error) {
      console.error('Error initializing audio:', error);
      throw error;
    }
  }

  private async playAudio(audioKey: string, config?: Partial<AudioConfig>): Promise<void> {
    try {
      await this.initialize();
      
      const audioConfig = { ...this.audioConfigs.get(audioKey), ...config };
      
      // Try Web Audio API first if preferred and available
      if (audioConfig.useWebAudio && this.audioContext && this.audioContext.state === 'running') {
        const buffer = this.audioBuffers.get(audioKey);
        if (buffer) {
          const source = this.audioContext.createBufferSource();
          source.buffer = buffer;
          
          // Apply volume if specified
          if (audioConfig.volume !== undefined && audioConfig.volume !== 1.0) {
            const gainNode = this.audioContext.createGain();
            gainNode.gain.value = audioConfig.volume;
            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);
          } else {
            source.connect(this.audioContext.destination);
          }
          
          source.start(0);
          return;
        }
      }

      // Fallback to HTMLAudioElement
      const audio = this.audioElements.get(audioKey);
      if (audio) {
        audio.currentTime = 0;
        if (audioConfig.volume !== undefined) {
          audio.volume = audioConfig.volume;
        }
        if (audioConfig.loop !== undefined) {
          audio.loop = audioConfig.loop;
        }
        await audio.play();
      }
    } catch (error) {
      console.error(`Error playing ${audioKey} audio:`, error);
    }
  }

  private stopAudio(audioKey: string): void {
    const audio = this.audioElements.get(audioKey);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }

  // Public methods using the unified system
  public playMissedTapSound(): void {
    // Use synchronous version for immediate feedback
    this.playAudioSync('failedHit');
  }

  private playAudioSync(audioKey: string): void {
    try {
      const audioConfig = this.audioConfigs.get(audioKey);
      
      // Try Web Audio API first if preferred and available
      if (audioConfig?.useWebAudio && this.audioContext && this.audioContext.state === 'running') {
        const buffer = this.audioBuffers.get(audioKey);
        if (buffer) {
          const source = this.audioContext.createBufferSource();
          source.buffer = buffer;
          source.connect(this.audioContext.destination);
          source.start(0);
          return;
        }
      }

      // Fallback to HTMLAudioElement
      const audio = this.audioElements.get(audioKey);
      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(error => {
          console.warn(`Error playing ${audioKey} with HTMLAudioElement:`, error);
        });
      }
    } catch (error) {
      console.error(`Error in playAudioSync for ${audioKey}:`, error);
    }
  }

  public async playUfoBreakSound(): Promise<void> {
    await this.playAudio('ufoBreak');
  }

  public async playEmojiCelebrateSound(): Promise<void> {
    await this.playAudio('emojiCelebrate');
  }

  public async playRescueSequence(): Promise<void> {
    try {
      await this.playUfoBreakSound();
      // Note: Emoji celebrate sound timing handled by RescueEffect component
    } catch (error) {
      console.error('Error playing rescue sequence:', error);
    }
  }

  public async playBackgroundMusic(): Promise<void> {
    await this.playAudio('background');
  }

  public stopBackgroundMusic(): void {
    this.stopAudio('background');
  }

  public stopGameStatusSounds(): void {
    this.stopAudio('gameOver');
    this.stopAudio('readyLevel');
  }

  public async playGameOverSound(): Promise<void> {
    await this.playAudio('gameOver');
  }

  public async playReadyLevelSound(): Promise<void> {
    await this.playAudio('readyLevel');
  }

  public async primeAudioContext(): Promise<void> {
    try {
      await this.initialize();
      if (!this.audioContext) return;
      
      // Create a silent buffer
      const buffer = this.audioContext.createBuffer(1, 1, this.audioContext.sampleRate);
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);
      source.start(0);
    } catch (error) {
      console.error('Error priming audio context:', error);
    }
  }

  public cleanup(): void {
    // Stop all audio elements
    this.audioElements.forEach(audio => {
        audio.pause();
        audio.src = '';
    });
    
    // Clear collections
    this.audioElements.clear();
    this.audioBuffers.clear();
    
    // Close audio context
    if (this.audioContext) {
      this.audioContext.close().catch(console.error);
    }
    
    this.audioContext = null;
  }
}

// Create a singleton instance
export const gameAudio = new GameAudioManager();